// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerDataSourceDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { observable, computed, action } from 'mobx';
import Paging from 'share/components/controls/DataGrid/helpers/Izenda.PagingModel';
import {
  BUNDLE,
  OBJECT_STATE,
  EMPTY_GUID,
  TABLE_DEF,
  ADVANCED,
  JOIN_TYPE,
  DATA_OBJECT_SWAP_KEYS,
  SEARCH_ALL,
  JOIN_TYPE_CROSS
} from 'logic/data/Izenda.ReportDesignerConstants';
import ReportDesignerRelationshipDetail from 'logic/models/Izenda.ReportDesignerRelationshipDetail';
import tracker from 'share/tracking/Izenda.Tracker';
import { getSelectedQuerySources } from 'modules/ReportDesigner/DataSource/Izenda.DataTreeUtils';
import {
  filter,
  sumBy,
  isEmpty,
  some,
  head,
  map,
  flow,
  compact,
  max,
  toString,
  toNumber,
  trim,
  remove,
  flatMap,
  groupBy,
  orderBy,
  find,
  differenceBy,
  lowerFirst,
  isNil,
  findIndex,
  isNull,
  isUndefined,
  reject,
  includes
} from 'lodash/fp';

@tracker
export default class DataSourceDetail {
  @observable dataSourceGrid = [];
  @observable dirtyDataSourceGrid = [];
  @observable pageInfo = new Paging(BUNDLE.SORT_ORDERS, BUNDLE.DEFAULT_CRITERIA);
  @observable totalRecords = 0;
  @observable aliasLevel = [];
  // used for stopping Swap Data Object of relationships in DataSource tab
  @observable isDataSourceGridDirty = false;
  // original relationships from API
  @observable dbRelationships = [];
  // loading relationship (dataSourceGrid) status -> used for handling 'No record found' issue
  @observable isLoadingRelationship = true;

  @computed
  get totalKeyJoin(): number {
    return sumBy('keyJoinCount', this.dataSourceGrid);
  }
  @computed
  get haveRelationships(): boolean {
    return !isEmpty(filter({ deleted: false }, this.dataSourceGrid));
  }
  @computed
  get displayDataSourceGrid(): Array<any> {
    return filter({ deleted: false, hidden: false }, this.dataSourceGrid);
  }

  /**
   * Get available alias
   * @param {any} dataItem
   */
  availableAlias(dataItem: any): Array<any> {
    return filter(alias => {
      return alias.level <= dataItem.level;
    }, this.aliasLevel);
  }

  /**
   * Check dirty
   */
  checkDirty(): boolean {
    return this.isDirty || some(['isDirty', true], this.dataSourceGrid);
  }

  /**
   * Clean dirty
   */
  @action
  cleanDirty(): void {
    this.resetDirty();

    this.dataSourceGrid.forEach(relationship => {
      relationship.cleanDirty && relationship.cleanDirty();
    });
  }

  /**
   * Reset grid
   */
  @action
  reset(): void {
    this.dataSourceGrid = [];
    this.pageInfo = new Paging(BUNDLE.SORT_ORDERS, BUNDLE.DEFAULT_CRITERIA);
  }

  /**
   * Add new relationship
   * @param {any} dataTree
   * @param {boolean} isAllQuerySourceUncategorized
   */
  @action
  addNew(dataTree: any, isAllQuerySourceUncategorized: boolean): any {
    const item = new ReportDesignerRelationshipDetail();
    item.setState(OBJECT_STATE.INSERT);

    if (dataTree && isAllQuerySourceUncategorized) {
      item.setJoinDataSourceCategory(EMPTY_GUID);
      item.setForeignDataSourceCategory(EMPTY_GUID);
    }

    this.dataSourceGrid.push(item);
    this.reindexLevelAndPosition(item);

    const selectedQuerySources = getSelectedQuerySources(dataTree);

    // Self join when only one query source is selected
    if (dataTree && selectedQuerySources.length === 1) {
      const selectedDataObject = head(selectedQuerySources);
      item.setDefaultValueByQuerySource(selectedDataObject, dataTree);
      this.initAliasForAddItem(item);
    }

    this.markDataSourceGridDirty();
    this.markDirty();
  }

  /**
   * Copy relationship
   * @param {any} relationship
   */
  @action
  copyRelationship(relationship: any) {
    const indexOfRelationship = this.dataSourceGrid.indexOf(relationship) + 1;
    const cloneObj = relationship.cloneRelationship();

    const item = new ReportDesignerRelationshipDetail(cloneObj);
    item.createDataForCopying();

    item.relationshipKeyJoins.forEach(keyJoin => {
      keyJoin.newId();
      keyJoin.setParentId(item.getId());
    });

    !isEmpty(relationship.joinQuerySourceId) && this.updateAlias(item);
    this.dataSourceGrid.splice(indexOfRelationship, 0, item);
    this.reindexLevelAndPosition(relationship);
    this.markDataSourceGridDirty();
    this.markDirty();
  }

  /**
   * Find relationship by id
   * @param {string} id
   */
  @action
  findRelationshipById(id: string) {
    return find({ tempId: id }, this.dataSourceGrid);
  }

  /**
   * Update alias for an item
   * @param {any} item
   */
  @action
  updateAlias(item: any) {
    const alias = this.getNewAlias(item.joinQuerySourceName);
    item.updateRelationshipItem(TABLE_DEF.ADVANCED_MODE.alias.key, alias, true);
    item.needAlias = true;

    this.updateAliasLevel(this.getAliasOption(item, alias), item);
  }

  /**
   * Get new auto increment alias
   * @param {string} joinQuerySourceName
   */
  getNewAlias(joinQuerySourceName: string) {
    const regex = `${joinQuerySourceName} (\\d+)$`;
    const nextNumber =
      (flow(
        remove({ state: OBJECT_STATE.DELETE }),
        map(x => toString(x.alias).match(regex)),
        compact,
        map(x => toNumber(x[1])),
        max
      )(this.dataSourceGrid) || 1) + 1;

    return `${joinQuerySourceName} ${nextNumber}`;
  }

  /**
   * Only call this function when user select only one Data Object.
   * The function will populate the alias automatically
   * @param  {any} item
   */
  @action
  initAliasForAddItem(item: any) {
    if (item.joinQuerySourceId === item.originalAliasId) {
      this.updateAlias(item);
    }
  }

  /**
   * Handle event when user select change on Data Object or foreign data object
   * @param  {any} item     Item has changed
   * @param  {string} keyEvent Data object or foreign data object field
   */
  @action
  onDataObjectChangeToGenerateAlias(item: any, keyEvent: string) {
    this.generateAliasForItem(item, keyEvent);
  }

  /**
   * Create alias for item
   * @param {any} item Item
   * @param  {string} keyEvent Data object or foreign data object field
   */
  generateAliasForItem(item: any, keyEvent: string) {
    if (!isEmpty(trim(item.alias)) || isEmpty(item.joinQuerySourceId)) {
      return;
    }

    const dataObjectKey = TABLE_DEF.ADVANCED_MODE.dataObject.key;
    const sameJoinQSRelationships = filter(
      ({ state, joinQuerySourceId, tempId }) =>
        state !== OBJECT_STATE.DELETE && joinQuerySourceId === item.joinQuerySourceId && tempId !== item.tempId,
      this.dataSourceGrid
    );

    if (this.isFirstSelfJoin(item)) {
      item.isSelfJoin = true;
      this.updateAlias(item);
    } else if (
      (isUndefined(keyEvent) || keyEvent === dataObjectKey) &&
      sameJoinQSRelationships.length > 0 &&
      some(({ alias }) => alias === '', sameJoinQSRelationships)
    ) {
      this.updateAlias(item);
    }
    this.markDirty();
  }

  /**
   * Generate aliases for relationships, only generate alias in 2 cases below:
   *  - is self join
   *  - querySourceId of current relationship is matched with prev relationships
   */
  generateAliases(): void {
    this.generateAliasForSelfJoin();
    this.dataSourceGrid.forEach(item => this.generateAliasForItem(item));
  }

  /**
   * Generate alias for self-join
   */
  generateAliasForSelfJoin() {
    this.dataSourceGrid.forEach(item => this.isSelfJoin(item) && item.alias === '' && this.updateAlias(item));
  }

  /**
   * Check if item is first self-join
   * @param {any} item
   */
  isFirstSelfJoin(item: any): boolean {
    return this.isSelfJoin(item) && !this.hasAlreadyFirstSelfJoin(item.joinQuerySourceId, item.id);
  }

  /**
   * Check if item is self-join
   * @param {any} item
   */
  isSelfJoin(item: any): boolean {
    return item.joinQuerySourceId === item.originalAliasId;
  }
  /**
   * Check if items before current item have self-join
   * @param {string} joinQuerySourceId
   * @param {string} id
   */
  hasAlreadyFirstSelfJoin(joinQuerySourceId: string, id: string) {
    const items = filter(o => {
      return o.isSelfJoin === true && o.joinQuerySourceId === joinQuerySourceId && id !== o.id;
    }, this.dataSourceGrid);

    return items.length > 0;
  }

  @action
  delete(item: any): any {
    // Delete in alias level
    this.updateAliasLevel(null, item);
    isEmpty(item.id) ? this.dataSourceGrid.splice(this.dataSourceGrid.indexOf(item), 1) : item.delete();

    this.markDataSourceGridDirty();
    this.markDirty();
  }

  /**
   * Populate Data
   * @param {any} dataSourceDetail
   * @param {any} changedDataObject
   * @param {number} reportMode
   */
  @action
  populateData(dataSourceDetail: any, changedDataObject: any, reportMode: number): any {
    if (reportMode === ADVANCED) {
      return this.populateDataForAdvancedMode(dataSourceDetail);
    } else {
      return this.populateDataForSimpleMode(dataSourceDetail);
    }
  }

  /**
   * Populate Data for Simple mode
   * @param {any} dataSourceDetail
   */
  @action
  populateDataForSimpleMode(dataSourceDetail: any): any {
    dataSourceDetail = dataSourceDetail || {};
    this.dataSourceGrid = dataSourceDetail.result;
    this.pageInfo.totalRecords = dataSourceDetail.total;

    return Promise.resolve(dataSourceDetail.relatedDataSourceCategories);
  }

  /**
   * Populate Data for Advanced mode
   * @param {any} dataSourceDetail
   */
  @action
  populateDataForAdvancedMode(dataSourceDetail: any): any {
    dataSourceDetail = dataSourceDetail || {};
    setKeyJoinParentId(dataSourceDetail);

    let dataSourceDetailResult = dataSourceDetail.result;
    if (this.isDataSourceGridDirty) {
      dataSourceDetailResult = this.mergeRelationships(dataSourceDetailResult);
    }
    this.populateDataSourceGrid(this.processDbRelationships(dataSourceDetailResult, this.dirtyDataSourceGrid));

    this.dataSourceGrid = this.filterNewRelationships(this.dataSourceGrid);
    this.pageInfo.totalRecords = dataSourceDetail.total;

    this.reindexLevelAndPosition();
    // Generate alias for relationships
    this.generateAliases();

    return Promise.resolve();
  }

  /**
   * Process swap list relationship to avoid generate alias
   * @param {Array<any>} dbRelationships
   */
  @action
  processDbRelationships(dbRelationships: Array<any>, dirtyRelationships: Array<any>) {
    const result = [];
    const numberOfRelationship = dbRelationships.length;

    // Index list relationship
    dbRelationships.forEach((item, index) => {
      item.relationshipPosition = index;
    });

    // Check if relationship is self join
    const isSelfJoin = item => item.joinQuerySourceId === item.originalAliasId;

    // Check if relationship should has alias
    const shouldHasAlias = item => item.alias || isSelfJoin(item);

    const compareQuerySourceToGroup = (relationship, group, key) => {
      return relationship[key] === group || (group === 'null' && isNull(relationship[key]));
    };

    // Relationships with alias will not be swap so they will be add at the beginning
    result.push(...filter(shouldHasAlias, dbRelationships));

    // Relationships which can be swap
    const swapableRelationships = reject(shouldHasAlias, dbRelationships);

    // Swap data objects
    while (result.length < numberOfRelationship) {
      const groups = flow(
        reject(shouldHasAlias),
        flatMap(rs => [rs.joinQuerySourceId, rs.foreignQuerySourceId]),
        groupBy(qs => qs)
      )(swapableRelationships);

      let numberOfLink = 1;
      let hadAdd = false;
      while (!hadAdd) {
        let minLength = 0;
        for (let group in groups) {
          if (groups.hasOwnProperty(group)) {
            if (groups[group].length === numberOfLink) {
              const relationshipIndexToAdd = findIndex(
                rs =>
                  rs.joinQuerySourceId === group ||
                  rs.foreignQuerySourceId === group ||
                  (group === 'null' && (isNull(rs.joinQuerySourceId) || isNull(rs.foreignQuerySourceId))),
                swapableRelationships
              );
              const relationshipToAdd = swapableRelationships[relationshipIndexToAdd];
              const isDirtyRelationship = some(
                rs =>
                  rs.id === relationshipToAdd.id ||
                  (rs.alias === relationshipToAdd.alias &&
                    (rs.joinQuerySourceId === relationshipToAdd.joinQuerySourceId &&
                      rs.joinFieldId === relationshipToAdd.joinFieldId &&
                      rs.selectedForeignAlias === relationshipToAdd.selectedForeignAlias &&
                      rs.foreignFieldId === relationshipToAdd.foreignFieldId)),
                dirtyRelationships
              );

              if (!isDirtyRelationship) {
                const alreadyHasJoinQuerySourceWithoutAlias = some(
                  rs => rs.joinQuerySourceId === group && rs.alias === '',
                  result
                );

                // Already has join query source in result => swap
                if (
                  (compareQuerySourceToGroup(relationshipToAdd, group, 'joinQuerySourceId') &&
                    alreadyHasJoinQuerySourceWithoutAlias) ||
                  (compareQuerySourceToGroup(relationshipToAdd, group, 'foreignQuerySourceId') &&
                    !alreadyHasJoinQuerySourceWithoutAlias)
                ) {
                  swapRelationshipDataObject(relationshipToAdd);
                }
              }

              result.push(relationshipToAdd);
              swapableRelationships.splice(relationshipIndexToAdd, 1);
              hadAdd = true;
              break;
            }
            if (minLength < groups[group].length) {
              minLength = groups[group].length;
            }
          }
        }
        numberOfLink = minLength;
      }
    }

    return orderBy('relationshipPosition', 'asc', result);
  }

  /**
   * Populate Data Source grid.
   * In case there are keyJoins which was not saving to database then we must keep them.
   * @param {Array<any>} combinedRelationships
   */
  @action
  populateDataSourceGrid(combinedRelationships: Array<any>) {
    // Delete relationships which were converted to keyjoin
    const deleteConvertedRelationships = relationships => {
      const convertedRelationshipIds = flow(
        flatMap(rs => rs.relationshipKeyJoins.slice()),
        map('convertedFromRelationshipId'),
        compact
      )(relationships);

      return convertedRelationshipIds.length < 1
        ? relationships
        : reject(rs => rs.state !== OBJECT_STATE.UPDATE && includes(rs.id, convertedRelationshipIds), relationships);
    };
    this.dataSourceGrid = deleteConvertedRelationships(combinedRelationships);
    this.dataSourceGrid.forEach(relationship => {
      this.updateAliasLevel(this.getAliasOption(relationship, relationship.alias), relationship);
    });
  }

  /**
   * Merge relationship in grid with data model relationship
   * @param {Array<any>} dbRelationships
   */
  @action
  mergeRelationships(dbRelationships: Array<any>) {
    const result = [];
    // Add relationship created by user
    // Relationship which were modify will be keep
    this.dataSourceGrid.forEach(gridRelationship => {
      const newRelationship = find({ tempId: gridRelationship.tempId }, dbRelationships);
      result.push(!gridRelationship.hasBeenModified && newRelationship ? newRelationship : gridRelationship);
    });

    // Add new db relationships which is not having in the grid
    return orderBy(['relationshipPosition'], ['asc'], result.concat(differenceBy('tempId', dbRelationships, result)));
  }

  /**
   * Filter new relationships/keyJoins: change status to hidden if searchText no match
   * @param {Array<any>} newRelationships
   */
  @action
  filterNewRelationships(newRelationships: Array<any>) {
    if (isEmpty(newRelationships)) return [];

    const searchText = this.pageInfo.searchText.toLowerCase();
    const searchProperty = lowerFirst(this.pageInfo.searchProperty);

    const updateHiddenStatus = relationship => {
      const isSearchTextMatched = value => {
        if (isNil(searchText) || searchText.length === 0) return true;
        return value && value.toLowerCase().indexOf(searchText) >= 0;
      };

      // recursive filtering for KeyJoins
      const filterKeyJoins = relationship => {
        if (!relationship.isKeyJoin) {
          relationship.relationshipKeyJoins = this.filterNewRelationships(relationship.relationshipKeyJoins);

          // only display parent relationship when at least 1 visible keyJoin
          const visibleKeyJoins = filter({ hidden: false }, relationship.relationshipKeyJoins);
          if (!isEmpty(visibleKeyJoins)) {
            relationship.setHidden(false);
          }
        }
      };

      const dbRelationship = find({ id: relationship.id }, newRelationships);
      if (isNil(dbRelationship)) {
        relationship.setHidden && relationship.setHidden(true);
      } else {
        // Filter all criterion in case 'ALL'
        if (searchProperty === SEARCH_ALL) {
          const criterion = BUNDLE.CRITERIA_LIST_ADVANCED_MODE;

          for (let index in criterion) {
            // Guard for in eslint
            if (index < criterion.length) {
              const criteriaKey = lowerFirst(criterion[index].value);
              relationship.setHidden && relationship.setHidden(!isSearchTextMatched(relationship[criteriaKey]));
              if (isSearchTextMatched(relationship[criteriaKey])) {
                break;
              }
            }
          }
        } else {
          relationship.setHidden && relationship.setHidden(!isSearchTextMatched(relationship[searchProperty]));
        }
      }

      filterKeyJoins(relationship);
    };

    newRelationships.map(updateHiddenStatus);
    return newRelationships;
  }

  /**
   * Update alias level
   * @param {string} newAlias
   * @param {any} item
   */
  @action
  updateAliasLevel(newAlias: string, item: any) {
    const existedLevel = find({ id: item.tempId }, this.aliasLevel);
    // Delete
    if (!newAlias) {
      const aliasIndex = this.aliasLevel.indexOf(existedLevel);
      if (aliasIndex >= 0) {
        this.aliasLevel.splice(aliasIndex, 1);
      }
      return;
    }

    // Add, Update
    if (!existedLevel) {
      this.aliasLevel.push({
        level: item.level,
        alias: newAlias,
        id: item.tempId,
        relationshipId: item.tempId
      });
    } else {
      existedLevel.alias = newAlias;
    }
  }

  /**
   * Remove alias level
   * @param {any} relationship
   */
  @action
  removeAliasLevel(relationship: any) {
    const removeIndex = findIndex({ id: relationship.tempId }, this.aliasLevel);
    removeIndex >= 0 && this.aliasLevel.splice(removeIndex, 1);
  }

  /**
   * Update list alias level
   * @param {any} item
   * @param {number} newLevel
   */
  @action
  updateAliasLevelOnly(item: any, newLevel: number) {
    const existedLevel = find({ id: item.tempId }, this.aliasLevel);
    if (existedLevel) {
      existedLevel.level = newLevel;
    }
  }

  /**
   * Reflect changed alias on all entire places
   * @param {Object} dataSourceItem
   */
  @action
  populateUpdatedAlias(dataSourceItem: any) {
    const { alias, previousAlias, joinQuerySourceId } = dataSourceItem;
    if (!isNull(alias)) {
      this.dataSourceGrid.forEach(dataSource => {
        dataSource.updateForeignAlias(joinQuerySourceId, alias, previousAlias);
        dataSource.updateKeyJoinForeignAlias(joinQuerySourceId, alias, previousAlias);
      });
    }
  }

  /**
   * Get alias option
   * @param {any} item
   * @param {string} aliasText
   */
  getAliasOption(item: any, aliasText: string) {
    return aliasText
      ? {
          name: aliasText,
          sourceId: item.joinQuerySourceId,
          aliasId: this.generateAliasId(item, aliasText),
          visible: true
        }
      : null;
  }

  /**
   * Generate alias id
   * @param {any} item
   * @param {string} aliasText
   */
  generateAliasId(item: any, aliasText: string) {
    return `${item.joinQuerySourceId}_${aliasText}`;
  }

  /**
   * Move relationship or key join from current index to new index
   * @param {any} currentRow
   * @param {any} newRow
   */
  @action
  moveRelationshipOrKeyJoin(currentRow: any, newRow: any) {
    const isParentCurrentRow = isEmpty(currentRow.parentId);
    const isParentNewRow = isEmpty(newRow.parentId);

    if (isParentCurrentRow && isParentNewRow) {
      // Moving relationship to relationship
      this.moveRelationship(currentRow, newRow);
    } else if (!isParentCurrentRow) {
      // Moving key join to relationship
      this.moveKeyJoin(currentRow, newRow);
    }

    this.markDirty();
    return Promise.resolve(true);
  }

  /**
   * Move relationship
   * @param {any} currentRow
   * @param {any} newRow
   */
  @action
  moveRelationship(currentRow: any, newRow: any) {
    // Find read index again because some rows is hidden
    const realCurrentInd = findIndex({ tempId: currentRow.tempId }, this.dataSourceGrid);
    const realNewInd = findIndex({ tempId: newRow.tempId }, this.dataSourceGrid);

    this.dataSourceGrid.splice(realCurrentInd, 1);
    this.dataSourceGrid.splice(realNewInd, 0, currentRow);
    this.updateDataAfterMoveSuccess(currentRow, newRow);
  }

  /**
   * Move key join
   * @param {any} currentRow
   * @param {any} newRow
   */
  @action
  moveKeyJoin(currentRow: any, newRow: any) {
    const currentParentRelationship = getRelationship(currentRow, this.dataSourceGrid);
    const newParentRelationship = getRelationship(newRow, this.dataSourceGrid);

    // return M04 error message if target relationship joinType is "Cross"
    if (newParentRelationship.joinType === JOIN_TYPE_CROSS) {
      return BUNDLE.MESSAGE.M04;
    }

    // find read index again because some rows is hidden
    const realCurrentInd = findIndex({ tempId: currentRow.tempId }, currentParentRelationship.relationshipKeyJoins);
    const realNewInd = isEmpty(newRow.parentId)
      ? newParentRelationship.relationshipKeyJoins
        ? newParentRelationship.relationshipKeyJoins.length
        : 0
      : findIndex({ tempId: newRow.tempId }, newParentRelationship.relationshipKeyJoins);

    // remove key join from current relationship, then append it to new relationship
    currentParentRelationship.relationshipKeyJoins.splice(realCurrentInd, 1);
    newParentRelationship.relationshipKeyJoins.splice(realNewInd, 0, currentRow);
    currentRow.setParentId(newParentRelationship.tempId);

    // update data after moving success
    this.updateDataAfterMoveSuccess(currentRow, newRow);
    currentParentRelationship.reindexKeyJoinsPosition();
    newParentRelationship.reindexKeyJoinsPosition();

    // Force the grid to be re-render because changes in child array do not trigger mobx re-render
    const firstRelationship = head(this.dataSourceGrid);
    this.dataSourceGrid.splice(0, 1, firstRelationship);
  }

  /**
   * Update data after moving relationship or key join
   * @param {any} currentRow
   * @param {any} newRow
   */
  @action
  updateDataAfterMoveSuccess(currentRow: any, newRow: any) {
    // Reindex level/position/alias level
    this.reindexLevelAndPosition();
    // Change state to UPDATE for current & new relationship
    currentRow.updateState();
    newRow.updateState();

    this.markDataSourceGridDirty();
  }

  /**
   * Reindex Alias Level & Relationship Position of DataSourceGrid.
   * Then update Alias Level from new row -> end
   * @param {any} fromRowItem
   */
  @action
  reindexLevelAndPosition(fromRowItem: any) {
    const currentRowLevel = fromRowItem ? fromRowItem.level : 1;
    this.dataSourceGrid.forEach((item: any, index: number) => {
      item.relationshipPosition = index;
      item.level = index + 1;

      item.relationshipKeyJoins.forEach(keyJoin => {
        keyJoin.level = index + 1;
      });

      if (item.level >= currentRowLevel) {
        this.updateAliasLevelOnly(item, index + 1);
      }
    });
  }

  /**
   * Get invalid AliasLevels of current row when moving to new row
   * @param {any} currentRow
   */
  getInvalidAliasLevelsForRow(currentRow: any) {
    const currentRelationship = getRelationship(currentRow, this.dataSourceGrid);
    const selectedForeignAlias = currentRow.isKeyJoin
      ? currentRow.selectedForeignSourceAlias
      : currentRelationship.selectedForeignAlias;
    return filter(
      o => currentRelationship.level <= o.level && selectedForeignAlias === o.alias.aliasId,
      this.aliasLevel
    );
  }

  /**
   * Check join type between two entities in one relationship
   * @param {any} currentRow
   */
  checkExistEntities(currentRow: any) {
    if (currentRow.joinQuerySourceId && currentRow.originalAliasId) {
      const existedItem = find(dataSource => {
        return (
          currentRow !== dataSource &&
          dataSource.state != OBJECT_STATE.DELETE &&
          ((dataSource.joinQuerySourceId === currentRow.joinQuerySourceId &&
            dataSource.originalAliasId === currentRow.originalAliasId &&
            dataSource.joinType !== currentRow.joinType) ||
            (dataSource.joinQuerySourceId === currentRow.originalAliasId &&
              dataSource.originalAliasId === currentRow.joinQuerySourceId &&
              ((dataSource.joinType !== currentRow.joinType &&
                (dataSource.joinType !== JOIN_TYPE.LEFT || currentRow.joinType !== JOIN_TYPE.RIGHT) &&
                (dataSource.joinType !== JOIN_TYPE.RIGHT || currentRow.joinType !== JOIN_TYPE.LEFT)) ||
                (dataSource.joinType === JOIN_TYPE.LEFT && currentRow.joinType === JOIN_TYPE.LEFT) ||
                (dataSource.joinType === JOIN_TYPE.RIGHT && currentRow.joinType === JOIN_TYPE.RIGHT)))) &&
          dataSource.alias === currentRow.alias
        );
      }, this.dataSourceGrid);

      return existedItem;
    }

    return null;
  }

  /**
   * Update join type between two entities in one relationship
   * @param {any} currentRow
   * @param {any} confirm
   */
  @action
  updateJoinTypeForSameEntities(currentRow: any, confirm: boolean) {
    if (currentRow.joinQuerySourceId && currentRow.originalAliasId) {
      map(dataSource => {
        if (
          currentRow !== dataSource &&
          dataSource.state != OBJECT_STATE.DELETE &&
          dataSource.joinQuerySourceId === currentRow.originalAliasId &&
          dataSource.originalAliasId === currentRow.joinQuerySourceId &&
          ((dataSource.joinType !== currentRow.joinType &&
            (dataSource.joinType !== JOIN_TYPE.LEFT || currentRow.joinType !== JOIN_TYPE.RIGHT) &&
            (dataSource.joinType !== JOIN_TYPE.RIGHT || currentRow.joinType !== JOIN_TYPE.LEFT)) ||
            (dataSource.joinType === JOIN_TYPE.LEFT && currentRow.joinType === JOIN_TYPE.LEFT) ||
            (dataSource.joinType === JOIN_TYPE.RIGHT && currentRow.joinType === JOIN_TYPE.RIGHT)) &&
          dataSource.alias === currentRow.alias
        ) {
          switch (confirm ? currentRow.joinType : dataSource.joinType) {
            case JOIN_TYPE.LEFT:
              if (confirm) {
                dataSource.joinType = JOIN_TYPE.RIGHT;
              } else {
                currentRow.joinType = JOIN_TYPE.RIGHT;
              }
              break;
            case JOIN_TYPE.RIGHT:
              if (confirm) {
                dataSource.joinType = JOIN_TYPE.LEFT;
              } else {
                currentRow.joinType = JOIN_TYPE.LEFT;
              }
              break;
            default:
              if (confirm) {
                dataSource.joinType = currentRow.joinType;
              } else {
                currentRow.joinType = dataSource.joinType;
              }
          }
        }

        if (
          currentRow !== dataSource &&
          dataSource.joinQuerySourceId === currentRow.joinQuerySourceId &&
          dataSource.originalAliasId === currentRow.selectedForeignAliass &&
          dataSource.joinType !== currentRow.joinType &&
          dataSource.alias === currentRow.alias
        ) {
          dataSource.joinType = currentRow.joinType;
        }

        if (dataSource.joinType === JOIN_TYPE_CROSS && currentRow !== dataSource) {
          dataSource.joinFieldId = null;
          dataSource.foreignFieldId = null;
        }
      }, this.dataSourceGrid);
      this.markDirty();
    }
  }

  /**
   * remove relationships which have DataObject/Foreign DataObject same with unchecked dataObject
   * @param {any} dataObject
   */
  @action
  removeUncheckRelationships(dataObject: any) {
    // Delete Alias level of unchecked DataObjects
    this.dataSourceGrid.forEach(relationship => {
      if (relationship.foreignQuerySourceId === dataObject.id || relationship.joinQuerySourceId === dataObject.id) {
        this.removeAliasLevel(relationship);
      }
    });

    // Filter out unchecked DataObjects
    this.dataSourceGrid.replace(
      filter(relationship => {
        return relationship.foreignQuerySourceId !== dataObject.id && relationship.joinQuerySourceId !== dataObject.id;
      }, this.dataSourceGrid)
    );

    // Reindex position before receive new relationship data so the position do not mess up
    this.reindexLevelAndPosition();
  }

  /**
   * Mark dataSourceGrid dirty
   */
  markDataSourceGridDirty() {
    this.isDataSourceGridDirty = true;
    this.dataSourceGrid.forEach(item => {
      item.setModify(true);
    });
    this.dirtyDataSourceGrid = this.dataSourceGrid;
  }

  /**
   * Reset data source grid dirty
   */
  resetDataSourceGridDirty() {
    this.isDataSourceGridDirty = false;
    this.dirtyDataSourceGrid = [];
  }

  /**
   * Get id from dbRelationships
   */
  getDbRelationshipIds() {
    return flow(
      remove({ state: OBJECT_STATE.DELETE }),
      map(item => ({
        alias: item.alias,
        joinQuerySourceId: item.joinQuerySourceId,
        selectedForeignAlias: item.selectedForeignAlias,
        relationshipPosition: item.relationshipPosition,
        hasBeenModified: item.hasBeenModified,
        convertedKeyJoinIds: flow(
          map('convertedFromRelationshipId'),
          compact
        )(item.relationshipKeyJoins.slice())
      }))
    )(this.dataSourceGrid);
  }

  /**
   * Validate data source grid
   */
  validateDataSourceGrid() {
    const invalidRows = filter(item => {
      return !isEmpty(this.getInvalidAliasLevelsForRow(item));
    }, this.dataSourceGrid);

    return isEmpty(invalidRows);
  }

  /**
   * Reset alias level
   */
  @action
  resetAliasLevel() {
    this.aliasLevel = [];
  }
}

function swapRelationshipDataObject(relationship) {
  const swapValue = (item, keys) => {
    const [firstKey, secondKey] = keys;
    const tempValue = item[firstKey];
    item[firstKey] = item[secondKey];
    item[secondKey] = tempValue;
  };

  DATA_OBJECT_SWAP_KEYS.forEach(keys => swapValue(relationship, keys));
  relationship.selectedForeignAlias = `${relationship.foreignQuerySourceId}_${relationship.foreignQuerySourceName}`;

  // switch joinType to LEFT/Right
  switch (relationship.joinType) {
    case JOIN_TYPE.LEFT:
      relationship.joinType = JOIN_TYPE.RIGHT;
      return;
    case JOIN_TYPE.RIGHT:
      relationship.joinType = JOIN_TYPE.LEFT;
      return;
    default:
      return;
  }
}

function setKeyJoinParentId(dataSourceDetail) {
  dataSourceDetail.result &&
    dataSourceDetail.result.forEach(rs => {
      rs.relationshipKeyJoins.forEach(keyJoin => {
        keyJoin.parentId = rs.id;
      });
    });
}

function getRelationship(item: any, dataSourceGrid: Array<any>) {
  return isEmpty(item.parentId) ? item : find({ tempId: item.parentId }, dataSourceGrid);
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportDesignerDataSourceDetail.js